<template>
  <v-container id="orchestration-rules" fluid>
    <v-card outlined v-if="orderWorkflow">
      <v-tabs grow v-model="active_tab">
        <v-tab>{{ $t('orderWorkflow.globalConfiguration') }}</v-tab>
        <v-tab>{{ $t('orderWorkflow.ruleManagement') }}</v-tab>
        <v-tabs-items v-model="active_tab" :touchless="dragAndDropEnabled">
          <!-- global Configuration tab -->
          <v-tab-item>
            <v-container fluid>
              <v-card outlined>
                <validation-observer ref="form">
                  <v-list-item v-if="!isNewWorkflow" class="mt-2">
                    <v-col cols="12" sm="6" lg="4" xl="2">
                      <v-row>
                        <v-text-field
                          id="copyReference"
                          v-model="orderWorkflow._id"
                          dense
                          disabled
                          hide-details="auto"
                          :label="$t('orderWorkflow.reference')"
                        />
                        <v-icon @click="copyReference()">mdi-content-copy</v-icon>
                      </v-row>
                    </v-col>
                  </v-list-item>
                  <v-list-item :class="isNewWorkflow ? 'mt-2' : ''">
                    <validation-provider
                      style="width: 100%"
                      rules="required"
                      :name="$t('orderWorkflow.name')"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        v-model="orderWorkflow.name"
                        dense
                        :error-messages="errors"
                        hide-details="auto"
                        :label="$t('orderWorkflow.name')"
                      />
                    </validation-provider>
                  </v-list-item>
                  <div class="pb-3">
                    <v-list-item>
                      <v-row>
                        <v-col cols="12" md="6" class="pb-0">
                          <v-switch
                            v-model="orderWorkflow.activated"
                            hide-details="auto"
                            :label="$t('orderWorkflow.activeWorkflow')"
                          />
                        </v-col>
                        <v-col cols="12" md="6" class="pb-0">
                          <v-switch
                            v-model="orderWorkflow.marketplace_activated"
                            hide-details="auto"
                            :label="$t('orderWorkflow.activateMarketplace')"
                          />
                        </v-col>
                      </v-row>
                    </v-list-item>
                    <v-list-item class="mb-2" v-if="availableDeliveryTypes.length">
                      <v-row>
                        <v-col cols="12" md="6" class="pb-0">
                          <v-switch
                            v-model="associateToADeliveryType"
                            hide-details="auto"
                            :label="$t('orderWorkflow.associateToADeliveryType')"
                          />
                        </v-col>
                        <v-col cols="12" md="6" class="pt-5">
                          <validation-provider
                            v-if="associateToADeliveryType"
                            style="width: 100%"
                            rules="required"
                            :name="$t('orderWorkflow.deliveryType')"
                            v-slot="{ errors }"
                          >
                            <v-select
                              dense
                              v-model="orderWorkflow.delivery_type"
                              :items="availableDeliveryTypes"
                              :error-messages="errors"
                              :label="$t('orderWorkflow.deliveryType')"
                              required
                              hide-details="auto"
                            ></v-select>
                          </validation-provider>
                        </v-col>
                      </v-row>
                    </v-list-item>
                  </div>
                </validation-observer>
              </v-card>
            </v-container>
          </v-tab-item>
          <!-- rule management tab -->
          <v-tab-item>
            <v-container fluid>
              <v-card outlined>
                <v-container fluid>
                  <v-btn
                    fab
                    small
                    color="rgb(100, 100, 100, 0.2)"
                    @click="dragAndDropEnabled = !dragAndDropEnabled"
                    fixed
                    right
                  >
                    <v-icon :color="dragAndDropEnabled ? 'primary' : ''">mdi-cursor-move</v-icon>
                  </v-btn>
                  <draggable
                    tag="v-row"
                    v-model="orderWorkflow.secondary_rules"
                    :disabled="!dragAndDropEnabled"
                    v-if="orderWorkflow.secondary_rules && orderWorkflow.secondary_rules.length"
                  >
                    <v-col
                      v-for="(secondary_rule, index) in orderWorkflow.secondary_rules"
                      :key="secondary_rule._id"
                      cols="12"
                      sm="6"
                      md="6"
                      lg="6"
                      xl="4"
                    >
                      <v-card outlined>
                        <v-list-item>
                          <v-list-item-content class="mb-0 pb-0">
                            <v-list-item-title class="title mb-2 text-center">
                              {{ $t('orderWorkflow.orchestrationRule') }} {{ index + 1 }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <order-workflows-form
                          :rule="secondary_rule"
                          :deliveryType="orderWorkflow.delivery_type"
                          :locations="locations"
                          :lists="lists"
                          :lists-loading="listsLoading"
                          :restore-context="restoreForRule(secondary_rule._id)"
                          @rule-form-validated="updateRule"
                          @rule-form-deleted="deleteRule"
                          @rule-form-enabled="setModificationFlag(index, true)"
                          @get-locations="getLocations"
                          @prepare-list-creation="prepareListCreation"
                        />
                      </v-card>
                    </v-col>
                  </draggable>
                  <v-toolbar class="mt-2" flat justify-content="center" height="auto">
                    <v-row justify="center">
                      <v-btn :disabled="addingRule" class="ma-2" color="primary" outlined @click="openNewForm">
                        {{ $t('orderWorkflow.addRule') }}
                      </v-btn>
                    </v-row>
                  </v-toolbar>
                </v-container>
              </v-card>
              <v-row>
                <v-col cols="12" sm="6" md="6" lg="6" xl="4">
                  <v-card v-if="addingRule" outlined>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title class="title mb-1 text-center">
                          {{ $t('orderWorkflow.newRule') }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <order-workflows-form
                      :createNew="true"
                      :deliveryType="orderWorkflow.delivery_type"
                      :locations="locations"
                      :lists="lists"
                      :lists-loading="listsLoading"
                      :restore-context="restoreForRule('new')"
                      @rule-form-validated="addRule"
                      @rule-form-cancelled="cancelNewRule"
                      @get-locations="getLocations"
                      @prepare-list-creation="prepareListCreation"
                    />
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
      <v-card-actions class="justify-center">
        <v-btn class="ma-2" color="primary" :disabled="loading" outlined @click="save">
          {{ $t('orderWorkflow.save') }}
        </v-btn>
        <v-btn
          class="ma-2"
          color="primary"
          :disabled="loading"
          outlined
          @click="confirmationDialogDeleteWorkflow = true"
          v-if="!isNewWorkflow"
        >
          {{ $t('orderWorkflow.delete') }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <confirmation-dialog
      :text="$t('orderWorkflow.deleteWorkflowModalText')"
      :title="$t('orderWorkflow.deleteWorkflowModalTitle')"
      :value="confirmationDialogDeleteWorkflow"
      @yes="deleteWorkflow"
      @no="confirmationDialogDeleteWorkflow = false"
    />
  </v-container>
</template>

<script>
  import orderWorkflowsForm from '../../components/orderWorkflows/OrderWorkflowsDetailsForm.vue'
  import draggable from 'vuedraggable'
  import { EventBus } from '@/event-bus'
  import { extend, ValidationObserver, ValidationProvider } from 'vee-validate'
  import { required } from 'vee-validate/dist/rules'
  import confirmationDialog from '../../components/ConfirmationDialog.vue'
  import { ListService } from '@/services/listService'
  import { saveContext, readContext, clearContext } from '@/helpers/listSelectorContext'

  extend('required', {
    ...required,
    message: 'This field is required',
  })
  export default {
    name: 'OrderWorkflowsDetails',
    components: {
      orderWorkflowsForm: orderWorkflowsForm,
      draggable: draggable,
      'validation-provider': ValidationProvider,
      'validation-observer': ValidationObserver,
      confirmationDialog: confirmationDialog,
    },
    data() {
      return {
        rules: [],
        active_tab: 0,
        addingRule: false,
        dragAndDropEnabled: false,
        orderWorkflow: {},
        availableDeliveryTypes: [],
        associateToADeliveryType: false,
        confirmationDialogDeleteWorkflow: false,
        loading: false,
        isNewWorkflow: this.$route.params.id === undefined,
        locations: undefined,
        lists: [],
        listsLoading: false,
        restoredRule: null,
        restoredCreatedListId: null,
      }
    },
    props: {
      alreadyAffectedDeliveryTypes: {
        type: Array,
        default: () => {
          return []
        },
      },
    },
    computed: {
      deliveryTypes: function () {
        return [
          { text: this.$t('orderWorkflow.homeDelivery'), value: 'home_delivery' },
          { text: this.$t('orderWorkflow.storeDelivery'), value: 'store_delivery' },
          { text: this.$t('orderWorkflow.relayPointDelivery'), value: 'relay_point_delivery' },
          { text: this.$t('orderWorkflow.expressHomeDelivery'), value: 'express_home_delivery' },
          { text: this.$t('orderWorkflow.clickAndReserve'), value: 'click_and_reserve' },
        ]
      },
    },
    mounted() {
      if (this.alreadyAffectedDeliveryTypes.length) {
        this.availableDeliveryTypes = this.deliveryTypes.filter(
          (deliveryType) => !this.alreadyAffectedDeliveryTypes.includes(deliveryType.value)
        )
      } else {
        this.availableDeliveryTypes = this.deliveryTypes
      }
      this.getLists()
      const ctx = readContext()
      if (ctx && ctx.returnTo === this.$route.fullPath) {
        if (this.isValidRestoreContext(ctx)) {
          this.restoreFromContext(ctx)
          return
        }
        // Malformed/stale context for this page (e.g. schema changed between
        // deploys while the tab stayed open) — drop it and fall back to the
        // normal flow rather than restoring a broken workflow.
        clearContext()
      }
      if (this.isNewWorkflow) {
        this.orderWorkflow = {
          activated: true,
          marketplace_activated: true,
          name: 'default workflow name',
          secondary_rules: [],
        }
      } else {
        this.get()
      }
    },
    methods: {
      get: function () {
        this.loading = true
        let path = '/v1/int/order-workflows/' + this.$route.params.id
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.$Amplify.API.get('order-workflows', path, myInit)
          .then((res) => {
            if (res.delivery_type) {
              let currentWorkflowDeliveryType = this.deliveryTypes.find(
                (deliveryType) => deliveryType.value === res.delivery_type
              )
              this.availableDeliveryTypes.push(currentWorkflowDeliveryType)
              this.associateToADeliveryType = true
            }
            this.orderWorkflow = res
            this.loading = false
          })
          .catch((e) => {
            if (e.response && e.response.status === 404) {
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('orderWorkflow.workflowNotFound'),
                color: 'error',
              })
            }
            this.$router.push({ name: 'OrderWorkflowsTable' })
            console.log(e)
            this.loading = false
          })
      },
      deleteWorkflow: function () {
        this.loading = true
        this.confirmationDialogDeleteWorkflow = false
        let path = '/v1/int/order-workflows/' + this.$route.params.id
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.$Amplify.API.del('order-workflows', path, myInit)
          .then(() => {
            this.$router.push({ name: 'OrderWorkflowsTable' })
            this.loading = false
          })
          .catch((e) => {
            console.log(e)
            this.loading = false
          })
      },
      sortRules: function (a, b) {
        if (a.position && b.position) {
          if (a.position > b.position) {
            return 1
          } else {
            return -1
          }
        } else {
          return 0
        }
      },
      save: function () {
        this.$refs.form.validate().then((success) => {
          if (success) {
            if (this.checkFormsAreValidated()) {
              this.loading = true
              let orderWorkflowToSave = this.unsetProvisionalIds()
              if (this.associateToADeliveryType === false) {
                orderWorkflowToSave.delivery_type = null
              }
              if (!orderWorkflowToSave._id) {
                let data = {
                  headers: {
                    Authorization: 'Bearer ' + this.$auth.accessToken,
                  },
                  body: orderWorkflowToSave,
                }
                this.$Amplify.API.post('order-workflows', '/v1/int/order-workflows', data)
                  .then(() => {
                    this.loading = false
                    EventBus.$emit('displaySnackbar', {
                      display: true,
                      text: this.$t('orderWorkflow.creationSuccessful'),
                      color: 'success',
                    })
                    this.$router.push({ name: 'OrderWorkflowsTable' })
                  })
                  .catch((e) => {
                    this.loading = false
                    console.log(e)
                    let errorMessage = this.$t('orderWorkflow.errorUpdatingWorkflowMessage')
                    if (e.response && e.response.data && e.response.data.message === 'error_delivery_type') {
                      errorMessage = this.$t('orderWorkflow.deliveryTypeAlreadyExists')
                    }
                    EventBus.$emit('displaySnackbar', {
                      display: true,
                      text: errorMessage,
                      color: 'error',
                    })
                  })
              } else {
                let data = {
                  headers: { Authorization: 'Bearer ' + this.$auth.accessToken },
                  body: orderWorkflowToSave,
                }
                this.$Amplify.API.put('order-workflows', '/v1/int/order-workflows/' + orderWorkflowToSave._id, data)
                  .then((res) => {
                    this.loading = false
                    this.orderWorkflow = res
                    EventBus.$emit('displaySnackbar', {
                      display: true,
                      text: this.$t('orderWorkflow.updateSuccessful'),
                      color: 'success',
                    })
                  })
                  .catch((e) => {
                    this.loading = false
                    console.log(e)
                    let errorMessage = this.$t('orderWorkflow.errorUpdatingWorkflowMessage')
                    if (e.response && e.response.data && e.response.data.message === 'error_delivery_type') {
                      errorMessage = this.$t('orderWorkflow.deliveryTypeAlreadyExists')
                    }
                    EventBus.$emit('displaySnackbar', {
                      display: true,
                      text: errorMessage,
                      color: 'error',
                    })
                  })
              }
            } else {
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('orderWorkflow.ruleNotValidatedErrorMessage'),
                color: 'error',
              })
            }
          }
        })
      },
      updateRule: function (formRuleData) {
        let modifiedRuleIndex = this.orderWorkflow.secondary_rules.findIndex((rule) => rule._id == formRuleData._id)
        this.orderWorkflow.secondary_rules[modifiedRuleIndex] = formRuleData
        this.resetRestoredRule()
      },
      deleteRule: function (ruleId) {
        let deletedRuleIndex = this.orderWorkflow.secondary_rules.findIndex((rule) => ruleId == rule._id)
        this.orderWorkflow.secondary_rules.splice(deletedRuleIndex, 1)
        this.resetRestoredRule()
      },
      addRule: function (newRuleData) {
        this.orderWorkflow.secondary_rules.push(newRuleData)
        this.addingRule = false
        this.resetRestoredRule()
      },
      cancelNewRule: function () {
        this.addingRule = false
        this.resetRestoredRule()
      },
      resetRestoredRule: function () {
        // The restore payload is consumed once, when the target rule form mounts.
        // Clearing it here prevents a later, unrelated rule form (notably another
        // "new" rule in the same session) from re-matching and inheriting stale
        // data such as a previously created list id.
        this.restoredRule = null
        this.restoredCreatedListId = null
      },
      openNewForm: function () {
        this.addingRule = true
        this.$vuetify.goTo(9999)
      },
      setModificationFlag: function (secondary_rule_index, activation) {
        /*
        do not use secondary rule directly as parameter because if you do, after the first updateRule,
        the flag in_modification won't change anymore
      */
        let secondary_rule = this.orderWorkflow.secondary_rules[secondary_rule_index]
        secondary_rule.in_modification = activation
      },
      checkFormsAreValidated: function () {
        return !this.orderWorkflow.secondary_rules.some((secondary_rule) => secondary_rule.in_modification === true)
      },
      unsetProvisionalIds: function () {
        const secondaryRulesCopy = this.orderWorkflow.secondary_rules.map((secondary_rule) => ({ ...secondary_rule }))
        for (let secondaryRule of secondaryRulesCopy) {
          if (secondaryRule._id.includes('provisory_id')) {
            secondaryRule._id = undefined
          }
        }
        return {
          _id: this.orderWorkflow._id,
          delivery_type: this.orderWorkflow.delivery_type,
          name: this.orderWorkflow.name,
          activated: this.orderWorkflow.activated,
          marketplace_activated: this.orderWorkflow.marketplace_activated,
          secondary_rules: secondaryRulesCopy,
        }
      },
      copyReference: function () {
        navigator.clipboard.writeText(this.orderWorkflow._id).then(
          () => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('orderWorkflow.copySuccessful'),
              color: 'success',
            })
          },
          () => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('orderWorkflow.copyFailed'),
              color: 'error',
            })
          }
        )
      },
      getLocations: function () {
        if (!this.locations) {
          this.loadingData = true
          let myInit = {
            headers: {
              Authorization: 'Bearer ' + this.$auth.accessToken,
            },
            queryStringParameters: {
              fields: ['location_id', 'name'],
              filters: JSON.stringify({ definitive_closure: false }),
              itemsPerPage: false,
            },
          }
          this.$Amplify.API.get('locations', '/v1/int/locations', myInit)
            .then((res) => {
              this.locations = res.data
              this.loadingData = false
            })
            .catch(() => {
              this.loadingData = false
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('orderWorkflow.locationsLoadingErrorMessage'),
                color: 'error',
              })
            })
        }
      },
      getLists: async function () {
        // Fetched once here and shared with every rule form via the `lists`
        // prop, so N rule cards no longer trigger N redundant identical calls.
        this.listsLoading = true
        try {
          const listService = new ListService(this.$auth.accessToken)
          const res = await listService.list({ type: 'locations' }, false)
          this.lists = res?.data || []
        } finally {
          this.listsLoading = false
        }
      },
      isValidRestoreContext: function (ctx) {
        return (
          !!ctx.page &&
          !!ctx.page.orderWorkflow &&
          Array.isArray(ctx.page.orderWorkflow.secondary_rules) &&
          !!ctx.rule &&
          typeof ctx.rule.key === 'string'
        )
      },
      prepareListCreation: function ({ mode, rule }) {
        saveContext({
          returnTo: this.$route.fullPath,
          page: {
            orderWorkflow: this.orderWorkflow,
            active_tab: this.active_tab,
            addingRule: this.addingRule,
            associateToADeliveryType: this.associateToADeliveryType,
          },
          rule,
        })
        this.$router.push({ name: 'listAdd', query: { mode } })
      },
      restoreForRule: function (key) {
        if (this.restoredRule && this.restoredRule.key === key) {
          return {
            formRule: this.restoredRule.formRule,
            createdListId: this.restoredCreatedListId,
          }
        }
        return undefined
      },
      restoreFromContext: function (ctx) {
        this.orderWorkflow = ctx.page.orderWorkflow
        this.active_tab = ctx.page.active_tab
        this.addingRule = ctx.page.addingRule
        this.associateToADeliveryType = ctx.page.associateToADeliveryType
        if (this.orderWorkflow.delivery_type) {
          const deliveryType = this.deliveryTypes.find((type) => type.value === this.orderWorkflow.delivery_type)
          if (deliveryType && !this.availableDeliveryTypes.some((type) => type.value === deliveryType.value)) {
            this.availableDeliveryTypes.push(deliveryType)
          }
        }
        this.restoredRule = ctx.rule
        this.restoredCreatedListId = ctx.createdListId || null
        clearContext()
      },
    },
  }
</script>
<style scoped>
  .v-slide-group__prev {
    display: none !important;
  }
</style>
